import { useCallback, useRef, useState } from 'react';
import { useWebAuthn } from '../contexts/WebAuthnContext';
import { getUserAuthContext } from './data/getUserAuthContext';
import {
  AuthState,
  classifyAuthError,
  resolveAuthState,
} from './useAuthStateModel';
import { AuthContext } from '../types/auth';
import {
  AuthActionButton,
  SmartAuthView,
  SmartButtonState,
  TRANSIENT_FAILURE_VIEW,
  isButtonEnabled,
  isResultCurrent,
  normalize,
  resolveForLookup,
  shouldSuppressRequest,
  viewForState,
} from './useSmartAuth.logic';

// Re-export the pure model/presentation layer so existing importers of this
// module keep working. The pure pieces live in useSmartAuth.logic.ts (free of
// React/WebAuthn imports) so they stay unit-testable.
export type {
  AuthMessage,
  AuthActionButton,
  SecondaryAction,
  SmartAuthView,
  SmartButtonState,
} from './useSmartAuth.logic';
export {
  isButtonEnabled,
  isResultCurrent,
  normalize,
  resolveForLookup,
  shouldSuppressRequest,
  viewForState,
} from './useSmartAuth.logic';

export interface UseSmartAuthReturn {
  username: string;
  setUsername: (u: string) => void;
  busy: boolean;
  state: AuthState | null;
  context: AuthContext | null;
  view: SmartAuthView | null;
  /** The single primary button the view binds to. */
  buttonState: SmartButtonState;
  /** Resolution-only: fetch context and resolve the view. Fires NO ceremony. */
  lookup: (email: string) => Promise<void>;
  /** Run a specific action button (the execution engine; ceremonies fire here). */
  runAction: (kind: AuthActionButton['kind']) => Promise<void>;
}

/**
 * Orchestrates the smart-login flow. Lookups (blur/Enter) resolve the view only;
 * ceremonies fire exclusively from runAction. The biometric prompt only runs on
 * an explicit click, sidestepping the browser's ambiguous NotAllowedError.
 */
export function useSmartAuth(): UseSmartAuthReturn {
  const { authenticate, register, requestDeviceAccess, setSnackbar } =
    useWebAuthn();
  const [username, setUsernameState] = useState('');
  const [busy, setBusy] = useState(false);
  // What the busy spinner should say — depends on the phase: a lookup
  // ("Looking you up…") vs a specific clicked action ("Signing in…" etc).
  const [busyLabel, setBusyLabel] = useState('Looking you up…');
  const [state, setState] = useState<AuthState | null>(null);
  const [context, setContext] = useState<AuthContext | null>(null);
  const [view, setView] = useState<SmartAuthView | null>(null);
  // The email a lookup has resolved for — the button enables only for this one.
  const [resolvedForEmail, setResolvedForEmail] = useState<string | null>(null);

  // Monotonic lookup sequence so an out-of-order/stale response is discarded.
  const lookupSeq = useRef(0);
  // The last email we filed a device request for (survives the form reset), so
  // a duplicate click in the pre-visible race window is suppressed.
  const justRequestedEmail = useRef<string | null>(null);

  // Every edit clears the resolution so the button instantly reverts to disabled.
  const setUsername = useCallback((u: string) => {
    setUsernameState(u);
    setResolvedForEmail(null);
  }, []);

  const applyState = useCallback(
    (next: AuthState, ctx: AuthContext, v: SmartAuthView) => {
      setState(next);
      setContext(ctx);
      setView(v);
    },
    [],
  );

  // Resolution-only. Fetch context, resolve the view, enable the button. Never
  // fires a ceremony. Honors the stale-email guard so late/stale results drop.
  const lookup = useCallback(
    async (email: string) => {
      const requested = normalize(email);
      if (!requested) return;
      const requestedSeq = ++lookupSeq.current;
      setBusyLabel('Looking you up…');
      setBusy(true);

      const { ctx, ok } = await getUserAuthContext(requested);

      const current = isResultCurrent(
        { requestedEmail: requested, requestedSeq },
        { currentEmail: email, currentSeq: lookupSeq.current },
      );
      if (!current) {
        // A newer lookup superseded this one. Only clear busy if we're latest.
        if (requestedSeq === lookupSeq.current) setBusy(false);
        return;
      }

      if (ok) {
        const { state: next, intent } = resolveForLookup(ctx);
        const v = viewForState(next, {
          ...intent,
          inviteStatus: ctx.invite?.status,
          email: requested,
        });
        applyState(next, ctx, v);
        // Observing a device row for this email clears the duplicate guard.
        if (ctx.deviceRequest) justRequestedEmail.current = null;
      } else {
        // Transient failure: surface a harmless re-check, NEVER a mutation.
        setContext(ctx);
        setState(null);
        setView(TRANSIENT_FAILURE_VIEW);
      }
      // Enable the button for this email either way (sign-in path or re-check).
      setResolvedForEmail(requested);
      setBusy(false);
    },
    [applyState],
  );

  // Execution engine. Fetch fresh context, route, and run the ceremony. Only
  // reached from runAction's ceremony kinds.
  const routeAndMaybeRun = useCallback(
    async (opts: {
      intendsAddDevice?: boolean;
      intendsNewAccount?: boolean;
    }) => {
      if (!username) return;
      setBusy(true);
      try {
        const { ctx } = await getUserAuthContext(username);
        const next = resolveAuthState(ctx, opts);
        applyState(
          next,
          ctx,
          viewForState(next, {
            ...opts,
            inviteStatus: ctx.invite?.status,
            email: normalize(username),
          }),
        );

        if (next === AuthState.ReadyToSignIn) {
          if (opts.intendsAddDevice || opts.intendsNewAccount) {
            // Approved device request or invite → run registration ceremony.
            await register(username, !!opts.intendsAddDevice);
          } else {
            // Existing credential on this device → authenticate. On failure,
            // re-route with the classified error for accurate messaging.
            //
            // The AppContent page-load spinner is gated on `initializing` (NOT
            // the global `loading` authenticate() toggles) precisely so a
            // failed ceremony's refined view survives rather than unmounting
            // <AuthPage/> mid-ceremony.
            try {
              await authenticate(username);
            } catch (err) {
              const errName = (err as { name?: string })?.name;
              const kind = classifyAuthError(errName);
              const refined = resolveAuthState(ctx, { lastError: kind });
              applyState(
                refined,
                ctx,
                viewForState(refined, {
                  inviteStatus: ctx.invite?.status,
                  email: normalize(username),
                }),
              );
            }
          }
        }
      } finally {
        setBusy(false);
      }
    },
    [username, applyState, authenticate, register],
  );

  const runAction = useCallback(
    async (kind: AuthActionButton['kind']) => {
      // Phase-appropriate spinner copy for the clicked action.
      const ACTION_BUSY_LABEL: Record<AuthActionButton['kind'], string> = {
        lookup: 'Looking you up…',
        signIn: 'Signing in…',
        retry: 'Signing in…',
        tryHybrid: 'Signing in…',
        addDevice: 'Adding this device…',
        setupNewAccount: 'Setting up your account…',
        requestAccess: 'Requesting approval…',
        checkAgain: 'Looking you up…',
      };
      setBusyLabel(ACTION_BUSY_LABEL[kind] ?? 'Working…');
      switch (kind) {
        case 'lookup':
          // Not resolved yet → resolve the user. This is the ONLY lookup entry
          // point; blur/Enter route here too.
          await lookup(username);
          break;
        case 'signIn':
        case 'retry':
          await routeAndMaybeRun({});
          break;
        case 'addDevice':
          await routeAndMaybeRun({ intendsAddDevice: true });
          break;
        case 'setupNewAccount':
          await routeAndMaybeRun({ intendsNewAccount: true });
          break;
        case 'tryHybrid':
          // Explicit authenticate from the device-unknown state so the browser
          // can offer its native phone/hybrid picker. A cancelled prompt
          // classifies to PasskeyDismissed (no credential expected here), not
          // an error.
          await routeAndMaybeRun({});
          break;
        case 'checkAgain':
          // Re-run the resolution-only lookup; never mutates.
          await lookup(username);
          break;
        case 'requestAccess': {
          // Duplicate-request guard: if we just filed for this email and the
          // device row isn't visible yet, don't file a second — show pending.
          if (
            shouldSuppressRequest({
              justRequestedEmail: justRequestedEmail.current,
              email: username,
              hasDeviceRequest: !!context?.deviceRequest,
            })
          ) {
            const v = viewForState(AuthState.DeviceRequestPending, {
              email: normalize(username),
            });
            applyState(AuthState.DeviceRequestPending, context!, v);
            setResolvedForEmail(normalize(username));
            break;
          }

          setBusy(true);
          try {
            const result = await requestDeviceAccess(username);
            if (result.ok) {
              setSnackbar({
                open: true,
                message: result.sourceIp
                  ? `Access requested from IP ${result.sourceIp}. An admin will approve it, then re-enter your email to continue.`
                  : 'Access requested. An admin will approve it, then re-enter your email to continue.',
                severity: 'info',
              });
              // Record the filed email BEFORE resetting the form.
              justRequestedEmail.current = normalize(username);
              // Reset to the empty-email state (button disabled).
              setUsernameState('');
              setResolvedForEmail(null);
              setState(null);
              setContext(null);
              setView(null);
            } else {
              setSnackbar({
                open: true,
                message: result.error || 'Request failed',
                severity: 'error',
              });
            }
          } finally {
            setBusy(false);
          }
          break;
        }
      }
    },
    [
      routeAndMaybeRun,
      lookup,
      requestDeviceAccess,
      username,
      context,
      applyState,
      setSnackbar,
    ],
  );

  // The button is the single state machine. Its `kind` IS the authority for
  // what a click does:
  //   • empty email           → disabled, nothing to do
  //   • busy                  → disabled, phase-appropriate spinner label
  //   • email not yet resolved→ kind 'lookup' ("Sign in"): clicking (or
  //                             blur/Enter) looks the user up and resolves it
  //   • resolved              → the resolved action (signIn / addDevice / …)
  // Because the ONLY transition into a lookup is from the 'lookup' kind, a
  // resolved button can never re-trigger a lookup — no dual-trigger desync.
  const buttonState: SmartButtonState = (() => {
    if (!normalize(username)) {
      return {
        label: 'Start Verification',
        kind: null,
        disabled: true,
        busy: false,
        secondary: null,
      };
    }
    if (busy) {
      return {
        label: busyLabel,
        kind: null,
        disabled: true,
        busy: true,
        secondary: null,
      };
    }
    if (isButtonEnabled(resolvedForEmail, username, busy) && view?.primary) {
      return {
        label: view.primary.label,
        kind: view.primary.kind,
        disabled: false,
        busy: false,
        secondary: view.secondary ?? null,
      };
    }
    // Not yet resolved for the current email → the button IS the lookup trigger.
    return {
      label: 'Start Verification',
      kind: 'lookup',
      disabled: false,
      busy: false,
      secondary: null,
    };
  })();

  return {
    username,
    setUsername,
    busy,
    state,
    context,
    view,
    buttonState,
    lookup,
    runAction,
  };
}
